import React, { useState } from 'react';
import { X, DollarSign, AlertCircle, CheckCircle } from 'lucide-react';
import ClientInvoiceGenerator from '../ClientInvoiceGenerator';

/**
 * Modal dédié à la génération de factures pour les bénéficiaires
 * Modal standalone indépendant du modal de détails
 */
const InvoiceGeneratorModal = ({ client, onClose, onSuccess }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState(null); // 'success' | 'error' | null
  const [errorMessage, setErrorMessage] = useState('');

  if (!client) return null;

  const handleSuccess = (result) => {
    setGenerationStatus('success');
    setIsGenerating(false);

    // Fermer automatiquement après 2 secondes
    setTimeout(() => {
      if (onSuccess) {
        onSuccess(result);
      }
      onClose();
    }, 2000);
  };

  const handleError = (error) => {
    setGenerationStatus('error');
    setErrorMessage(error.message || 'Une erreur est survenue lors de la génération de la facture');
    setIsGenerating(false);
  };

  const handleCloseClick = () => {
    if (!isGenerating) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 dark:bg-opacity-70 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* En-tête du modal */}
        <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-emerald-700 dark:from-emerald-700 dark:to-emerald-800 px-6 py-4 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Génération de facture</h3>
                <p className="text-emerald-100 text-sm mt-0.5">
                  {client.firstName} {client.lastName}
                </p>
              </div>
            </div>
            <button
              onClick={handleCloseClick}
              disabled={isGenerating}
              className={`p-2 rounded-lg transition-colors ${
                isGenerating
                  ? 'bg-white/10 text-white/50 cursor-not-allowed'
                  : 'bg-white/10 hover:bg-white/20 text-white'
              }`}
              title={isGenerating ? 'Génération en cours...' : 'Fermer'}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Corps du modal */}
        <div className="p-6">
          {/* Informations du client */}
          <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Informations du bénéficiaire
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">Nom complet:</span>
                <div className="font-medium text-gray-900 dark:text-white">
                  {client.firstName} {client.lastName}
                </div>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Email:</span>
                <div className="font-medium text-gray-900 dark:text-white">
                  {client.email || 'Non renseigné'}
                </div>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Téléphone:</span>
                <div className="font-medium text-gray-900 dark:text-white">
                  {client.phoneNumber || client.phone || 'Non renseigné'}
                </div>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Statut:</span>
                <div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    client.isActive !== false
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                  }`}>
                    {client.isActive !== false ? 'Actif' : 'Inactif'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Messages de statut */}
          {generationStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-semibold text-green-800 dark:text-green-200 mb-1">
                    Facture générée avec succès !
                  </h4>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    La facture a été créée et est maintenant disponible dans le module comptabilité.
                    Le client pourra la consulter et la payer depuis son espace personnel.
                  </p>
                </div>
              </div>
            </div>
          )}

          {generationStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">
                    Erreur lors de la génération
                  </h4>
                  <p className="text-sm text-red-700 dark:text-red-300">
                    {errorMessage}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Composant de génération de facture */}
          {!generationStatus && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800">
              <ClientInvoiceGenerator
                client={client}
                onClose={() => {
                  if (!isGenerating) {
                    onClose();
                  }
                }}
                onSuccess={handleSuccess}
                onError={handleError}
                onGeneratingChange={setIsGenerating}
              />
            </div>
          )}

          {/* Instructions */}
          {!generationStatus && (
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2 flex items-center">
                <AlertCircle className="w-4 h-4 mr-2" />
                Informations importantes
              </h4>
              <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1.5 ml-6">
                <li className="list-disc">
                  Sélectionnez la période de facturation (mois en cours, dernier mois, ou période personnalisée)
                </li>
                <li className="list-disc">
                  Le montant sera calculé automatiquement en fonction des heures de rendez-vous
                </li>
                <li className="list-disc">
                  Seuls les rendez-vous avec statut "assigné" ou "complété" sont facturables
                </li>
                <li className="list-disc">
                  La facture sera visible dans "Comptabilité → Gestion des factures"
                </li>
                <li className="list-disc">
                  Le client recevra un accès à la facture dans son espace personnel
                </li>
              </ul>
            </div>
          )}
        </div>

        {/* Pied du modal */}
        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 px-6 py-4 rounded-b-xl border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {isGenerating && (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600 mr-2"></div>
                  Génération en cours...
                </div>
              )}
            </div>
            <button
              onClick={handleCloseClick}
              disabled={isGenerating}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isGenerating
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              {generationStatus === 'success' ? 'Fermeture automatique...' : 'Fermer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceGeneratorModal;
